import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { TrackingEvents, sendLoggingEvents } from '../../events';
import { useGetTelehealthSessionInfo } from '../../hooks';
import { useAdmissionFlow } from '../../hooks/useAdmissionFlow';
import { useVideoPlatform } from '../../hooks/useVideoPlatform';
import { useVisitorId } from '../useVisitorId';
import { admissionTimeAtom } from '../waiting-room/admission/state';

function getAdmitToEnterMs(admissionTime: string | undefined) {
  if (!admissionTime) return undefined;
  const admissionTimestamp = Date.parse(admissionTime);
  if (Number.isNaN(admissionTimestamp)) return undefined;
  return Date.now() - admissionTimestamp;
}

export function useClientEnterSessionRoomLogger() {
  const { patientShortId, providerShortId } = useParams<{
    patientShortId: string;
    providerShortId: string;
  }>();
  const visitorUuid = useVisitorId();
  const admissionTime = useAtomValue(admissionTimeAtom);
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const appointmentShortId = data?.telehealthSessionInfo?.appointment?.shortId ?? '';
  const joinRoomMode = useAdmissionFlow();
  const videoPlatform = useVideoPlatform();
  const admitToEnterMs = useMemo(() => getAdmitToEnterMs(admissionTime), [admissionTime]);
  const hasLoggedEnterRef = useRef(false);

  useEffect(
    function sendLoggingEventsOnPageLoad() {
      if (hasLoggedEnterRef.current) return;
      sendLoggingEvents(TrackingEvents.CLIENT_ENTER_SESSION_ROOM, {
        visitorUuid,
        patientShortId: patientShortId ?? '',
        providerShortId: providerShortId ?? '',
        appointmentShortId,
        joinRoomMode,
        videoPlatform,
        admissionTime,
        ...(admitToEnterMs !== undefined ? { admitToEnterMs } : {}),
      });
      hasLoggedEnterRef.current = true;
    },
    [
      admitToEnterMs,
      admissionTime,
      appointmentShortId,
      joinRoomMode,
      patientShortId,
      providerShortId,
      videoPlatform,
      visitorUuid,
    ],
  );
}
